import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DealerStateContext } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import MapPin from "@insite/mobius/Icons/MapPin";
import Phone from "@insite/mobius/Icons/Phone";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreDetailsAddressInfoCondensedDisplay from "@WhiteCap/Components/StoreDetailsAddressInfoCondensedDisplay";
import StoreDetailsGoogleMapsDirectionLink from "@WhiteCap/Components/StoreDetailsGoogleMapsDirectionLink";
import { formattedPhoneNumber } from "@WhiteCap/Utils/GetFormattedPhoneNumber";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import parse from "html-react-parser";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    locationFinderUrl: getPageLinkByPageType(state, "LocationFinderPage")?.url,
});

type Props = ReturnType<typeof mapStateToProps> & HasHistory;

export interface CustomDealerInformationStyles {
    parentContainer?: InjectableCss;
    mainContainer?: GridContainerProps;
    container?: GridContainerProps;
    selectedDealerDisplayBackToResultsGridItem?: GridItemProps;
    selectedDealerDisplayBackToResultsLink?: LinkPresentationProps;
    nameGridItem?: GridItemProps;
    nameText?: TypographyPresentationProps;
    addressInfoGridItem?: GridItemProps;
    addressInfoIcon?: IconPresentationProps;
    addressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    phoneGridItem?: GridItemProps;
    phoneLink?: LinkPresentationProps;
    distanceGridItem?: GridItemProps;
    distanceDisplayText?: TypographyPresentationProps;
    htmlContentGridItem?: GridItemProps;
    googleMapsDirectionLinkGridItem?: GridItemProps;
    googleMapsDirectionLink?: LinkPresentationProps;
    visitWebsiteLinkGridItem?: GridItemProps;
    visitWebsiteLink?: LinkPresentationProps;
    boldTitle?: InjectableCss;
    blackColour?: InjectableCss;
    htmlContentTitle?: InjectableCss;
    htmlContent?: InjectableCss;
}

export const dealerInformationStyles: CustomDealerInformationStyles = {
    parentContainer: {
        css: css`
            padding: 10px;
        `,
    },
    mainContainer: {
        gap: 10,
        css: css`
            border-top: 3px solid ${WhitecapColors.orange1} !important;
        `,
    },
    container: {
        gap: 10,
        css: css``,
    },
    selectedDealerDisplayBackToResultsGridItem: {
        width: 12,
    },
    selectedDealerDisplayBackToResultsLink: {
        icon: {
            iconProps: {
                src: ChevronLeft,
            },
            position: "left",
        },
    },
    nameGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
        `,
    },
    nameText: {
        color: `${WhitecapColors.lightBlack}`,
        weight: "bold",
        ellipsis: true,
        size: "20px",
        css: css`
            width: 100%;
            padding: 20px 0px 10px;
            margin-bottom: 10px;
            line-height: 28px;
            border-bottom: 1px solid ${WhitecapColors.gray2} !important;
        `,
    },
    addressInfoGridItem: {
        width: 12,
        css: css`
            margin-bottom: 10px !important;
            padding: 0 20px 0 10px;
            display: flex;
            gap: 16px;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    addressInfoIcon: {
        src: "MapPin",
        color: "primary",
    },
    addressInfoCondensed: {
        wrapper: {
            css: css`
                font-style: normal;
            `,
        },
    },
    phoneGridItem: {
        width: 12,
        css: css`
            padding: 0 0 0 10px;
            margin-bottom: 10px !important;
            align-items: flex-end;
        `,
    },
    phoneLink: {
        typographyProps: {
            css: css``,
        },
    },
    htmlContentGridItem: {
        width: 12,
        css: css`
            padding: 0 0 20px 10px;
            margin-bottom: 30px !important;
        `,
    },
    googleMapsDirectionLinkGridItem: {
        width: 12,
        css: css`
            padding: 0 0 0 10px;
            margin-bottom: 10px !important;
        `,
    },
    visitWebsiteLinkGridItem: {
        width: 6,
    },
    boldTitle: {
        css: css`
            font-size: 16px;
            color: ${WhitecapColors.lightBlack};
            font-weight: 600 !important;
        `,
    },
    blackColour: {
        css: css`
            font-size: 16px;
            color: ${WhitecapColors.lightBlack};
            > span {
                color: ${WhitecapColors.lightBlack} !important;
            }
        `,
    },
    htmlContentTitle: {
        css: css`
            color: ${WhitecapColors.lightBlack};
            font-size: 20px;
            font-weight: 700;
            padding: 20px 0px 10px;
            margin: 0;
            margin-bottom: 10px;
            border-bottom: 1px solid ${WhitecapColors.gray2} !important;
        `,
    },
    htmlContent: {
        css: css`
            color: ${WhitecapColors.lightBlack};
            font-size: 16px;
            > p {
                margin-bottom: 10px;
                color: ${WhitecapColors.lightBlack};
                font-size: 16px;
            }
        `,
    },
};

const styles = dealerInformationStyles;

const CustomDealerInformation: React.FC<Props> = ({ history, locationFinderUrl }) => {
    const { value: dealer } = useContext(DealerStateContext);
    if (!dealer) {
        return null;
    }

    const handleBackToResults = () => {
        history.push(locationFinderUrl!);
    };

    const StoreHours = ({ timing }: { timing: string }) => {
        let formattedTiming = timing;

        formattedTiming = formattedTiming.replace(/:00 \b/g, "");

        return (
            <StyledWrapper>
                <Typography as="b" {...styles.boldTitle}>
                    {translate("Store Hours")}:
                </Typography>
                <Typography as="p" {...styles.blackColour} style={{ marginBottom: "10px" }}>
                    {translate("Mon - Fri")}: {formattedTiming}
                </Typography>
                <Typography as="p" {...styles.blackColour}>
                    {translate("Sat - Sun: Closed")}
                </Typography>
            </StyledWrapper>
        );
    };

    const PhoneNumber = ({ number }: { number: string }) => {
        const formattedNumber = formattedPhoneNumber(number);
        return (
            <Link href={`tel:${number}`} {...styles.blackColour} target="_blank">
                &nbsp;{formattedNumber}
            </Link>
        );
    };

    return (
        <StyledWrapper {...styles.parentContainer}>
            <GridContainer
                {...styles.mainContainer}
                data-test-selector="dealerInformation"
                data-test-key={`${dealer.id}`}
            >
                <GridItem {...styles.nameGridItem}>
                    <Typography as="h2" {...styles.nameText}>
                        {dealer.name}
                    </Typography>
                </GridItem>

                <GridItem {...styles.addressInfoGridItem}>
                    <StoreDetailsAddressInfoCondensedDisplay {...dealer} extendedStyles={styles.addressInfoCondensed} />
                    {dealer.properties && dealer.properties.timing && (
                        <StyledWrapper>
                            <StoreHours timing={dealer.properties.timing} />
                        </StyledWrapper>
                    )}
                    {dealer.properties && dealer.properties.storeManager && (
                        <StyledWrapper>
                            <Typography as="b" {...styles.boldTitle}>
                                {translate("Store Manager:")}
                            </Typography>
                            <Typography as="p" {...styles.blackColour}>
                                {dealer.properties.storeManager}
                            </Typography>
                        </StyledWrapper>
                    )}
                </GridItem>

                <GridItem {...styles.googleMapsDirectionLinkGridItem}>
                    <StoreDetailsGoogleMapsDirectionLink {...styles.googleMapsDirectionLink} {...dealer} />
                </GridItem>

                {dealer.phone && (
                    <GridItem {...styles.phoneGridItem}>
                        <Typography as="b" {...styles.boldTitle}>
                            {`${translate("Phone")}:`}
                        </Typography>

                        <PhoneNumber number={dealer.phone} />
                    </GridItem>
                )}

                {dealer.htmlContent && (
                    <>
                        <GridItem {...styles.htmlContentGridItem}>
                            <StyledWrapper {...styles.container} data-test-selector="dealerInformation_htmlContent">
                                <Typography as="h2" {...styles.htmlContentTitle}>
                                    {translate("Store Details:")}
                                </Typography>

                                <StyledWrapper {...styles.htmlContent}>
                                    {parse(dealer.htmlContent, parserOptions)}
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    </>
                )}

                {dealer.webSiteUrl && (
                    <GridItem {...styles.visitWebsiteLinkGridItem}>
                        <Link href={dealer.webSiteUrl} target="_blank" {...styles.visitWebsiteLink}>
                            {translate("Visit Website")}
                        </Link>
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(CustomDealerInformation)),
    definition: {
        allowedContexts: ["DealerDetailsPage"],
        group: "Dealer Details",
    },
};

export default widgetModule;
